import { effect } from '@angular/core';
import { inject as injectBranding } from '@angular/core';
import { SiteBrandingService } from '../../services/site-branding.service';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy
} from '@angular/core';
import * as L from 'leaflet';

import { LocationService } from '../../services/location.service';
import { Location } from '../../models/app.models';

@Component({
  selector: 'app-location-map',
  imports: [],
  templateUrl: './location-map.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./location-map.component.css']
})
export class LocationMapComponent implements AfterViewInit, OnDestroy {
  readonly branding = injectBranding(SiteBrandingService);

  private readonly refreshMarkerIcons = effect(() => {
    const url = this.branding.logoUrl();
    const image = document.createElement('img');
    image.src = url;
    image.alt = 'Site logo';
    const icon = L.divIcon({ className: 'mc-marker', html: image.outerHTML,
      iconSize: [32, 32], iconAnchor: [16, 16], popupAnchor: [0, -20] });
    this.markers?.forEach(marker => marker.setIcon(icon));
  });

  private map: L.Map | undefined;

  private markers:
    Map<number, L.Marker> = new Map();

  selectedIndex: number | null = null;

  locations: Location[] = [];

  constructor(
    private readonly locationService: LocationService
  ) {}

  ngAfterViewInit(): void {
    this.initMap();
    this.loadLocations();

    setTimeout(() => {
      this.map?.invalidateSize(true);
    }, 500);
  }

  ngOnDestroy(): void {
    this.map?.remove();
  }

  selectLocation(index: number): void {
    const location =
      this.locations[index];

    const marker =
      this.markers.get(index);

    if (!location) {
      return;
    }

    this.selectedIndex = index;

    this.map?.flyTo(
      [
        location.latitude,
        location.longitude
      ],
      14,
      {
        duration: 0.8
      }
    );

    marker?.openPopup();
  }

  // ============================================================
  // LOCATIONS
  // ============================================================

  private loadLocations(): void {
    this.locationService
      .getLocations()
      .subscribe({
        next: locations => {
          this.locations = locations;

          this.addMarkers();
          this.fitMapToLocations();
        },
        error: error => {
          console.error(
            'Unable to load locations:',
            error
          );
        }
      });
  }

  // ============================================================
  // MAP
  // ============================================================

  private initMap(): void {
    this.map = L.map('map', {
      center: [40.2, -82.5],
      zoom: 6,
      zoomControl: false,
      scrollWheelZoom: true,
      maxBounds: [
        [-90, -180],
        [90, 180]
      ],
      maxBoundsViscosity: 1.0,
      minZoom: 2
    });

    L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        tileSize: 256,
        zoomOffset: 0
      }
    ).addTo(this.map);
  }

  private fitMapToLocations(): void {
    if (!this.map) {
      return;
    }

    const validLocations =
      this.locations.filter(location =>
        Number.isFinite(location.latitude) &&
        Number.isFinite(location.longitude)
      );

    // No locations:
    // keep the default Ohio-centered fallback.
    if (validLocations.length === 0) {
      this.map.setView(
        [40.2, -82.5],
        6
      );

      return;
    }

    // One location:
    // center directly on the store without zooming
    // excessively close.
    if (validLocations.length === 1) {
      const location =
        validLocations[0];

      this.map.setView(
        [
          location.latitude,
          location.longitude
        ],
        12
      );

      return;
    }

    // Multiple locations:
    // calculate the smallest bounds containing all stores.
    const bounds =
      L.latLngBounds(
        validLocations.map(location => [
          location.latitude,
          location.longitude
        ] as L.LatLngTuple)
      );

    this.map.fitBounds(
      bounds,
      {
        padding: [50, 50],
        maxZoom: 11
      }
    );
  }

  // ============================================================
  // MARKERS
  // ============================================================

  private addMarkers(): void {
    if (!this.map) {
      return;
    }

    this.markers.clear();

    const icon = L.divIcon({
      className: 'mc-marker',
      html: `
        <img
          src="${this.branding.logoUrl()}"
          alt="McDonald's"
        />
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -20]
    });

    this.locations.forEach(
      (location, index) => {

        if (
          !Number.isFinite(location.latitude) ||
          !Number.isFinite(location.longitude)
        ) {
          return;
        }

        const marker = L.marker(
          [
            location.latitude,
            location.longitude
          ],
          {
            icon
          }
        )
          .addTo(this.map!)
          .bindPopup(`
            <div class="mc-popup">
              <strong>
                ${location.name}
              </strong>

              <span class="popup-address">
                ${location.address}
              </span>

              <span class="popup-hours">
                ${location.hours}
              </span>

              <div class="popup-actions">

                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="popup-btn directions"
                >
                  Get Directions
                </a>

                <a
                  href="tel:${location.phone}"
                  class="popup-btn call"
                >
                  Call Store
                </a>

              </div>
            </div>
          `);

        marker.on(
          'click',
          () => {
            this.selectedIndex = index;
          }
        );

        this.markers.set(
          index,
          marker
        );
      }
    );
  }
}
